import React, { useState, useMemo, useEffect } from 'react'; // Added useEffect
import { FavoriteBorder, Star, AddShoppingCart } from '@mui/icons-material';
import {
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import axios from 'axios'; // Import axios

import { useShop } from '../context/ShopContext';
// 🛑 Mangalsutra Banner Image URL (आप इसे किसी वास्तविक इमेज से बदल सकते हैं)
const MANGALSUTRA_BANNER_IMAGE = 'https://csjewels.com/_next/image?url=https%3A%2F%2Fbackend.csjewels.com%2Fuploads%2F2%2F2025-08%2Fdesktop_banner_1283x430px_02.jpg&w=3840&q=75';


// ---
// Product Data Type (Unified API Structure)
// ---

interface Product {
  id: number; // Using srno from backend as id
  itemId?: number; // Optional
  imageUrl: string; // The URL for the image
  name: string; // itemName from backend
  price: number; // price from backend
  stock: number; // stockQuantity from backend
  category: string; // e.g., 'Mangalsutra'
  rating: number; // Added rating for sorting/display
  style: string; // e.g., 'Short', 'Long', 'Pendant', 'Traditional'
  stone_metal: string; // e.g., 'Gold', 'Diamond', 'Black Beads'
}

// Updated filter options for Mangalsutras
const filterOptions = {
  stone_metal: ['Gold', 'Diamond', 'Kundan', 'Black Beads'],
  style: ['Short (Daily Wear)', 'Long', 'Pendant Style', 'Traditional', 'Maharashtrian'],
};

// ---
// Product Card Component (Updated to use Product interface)
// ---

interface ProductCardProps {
  product: Product; 
  onAddToCart: (item: any) => Promise<void>; 
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  

const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToCart({
        productId: String(product.id),
        productTitle: product.name,
        productImage: product.imageUrl,
        price: product.price,
        quantity: 1
    });
};
  
  const handleAddToWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    alert(`Added "${product.name}" to Wishlist! (Dummy Action)`);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transform transition-all duration-300 hover:shadow-xl hover:scale-[1.02] cursor-pointer">
      
      <div className="relative w-full h-72 overflow-hidden">
        <div className="w-full h-full">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
          />
        </div>
        
        {/* <button
          className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md text-red-500 hover:text-red-700 transition"
          aria-label="Add to Wishlist"
          onClick={handleAddToWishlist}
        >
          <FavoriteBorder fontSize="small" />
        </button> */}
      </div>
      
      <div className="p-3 text-center">
        <span className="text-xs font-semibold uppercase text-amber-700 bg-amber-100 px-2 py-1 rounded mb-1 inline-block">
            {product.stone_metal || 'JEWELLERY'}
        </span>
        <p className="block text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis">
          {product.name}
        </p>
        
        <p className="text-lg font-bold text-amber-900 mt-1">
          ₹{product.price.toLocaleString('en-IN')}
        </p>

        <div className="flex justify-center items-center mt-1">
          <Star sx={{ fontSize: 16, color: 'gold' }} />
          <span className="text-sm text-gray-600 ml-1">{(product.rating || 4.5).toFixed(1)}</span>
        </div>
        
        <button 
          className="mt-3 w-full py-2 flex items-center justify-center gap-2 bg-amber-700 text-white rounded-md hover:bg-amber-800 transition"
          onClick={handleAddToCart}
        >
          <AddShoppingCart fontSize="small" />
          <span>Add To Cart</span>
        </button>
      </div>
    </div>
  );
};

// ---
// Filter Sidebar Component
// ---

const FilterSidebar: React.FC<{ 
  filters: typeof filterOptions,
  selectedFilters: Record<string, string[]>,
  onFilterChange: (category: string, value: string) => void
}> = ({ filters, selectedFilters, onFilterChange }) => {

  const renderCheckboxes = (category: keyof typeof filterOptions, title: string) => (
    <div className="mb-4">
      <h4 className="font-semibold text-gray-700 mb-2">{title}</h4>
      <List dense className="p-0">
        {filters[category].map((item) => (
          <ListItem key={item} disablePadding className="py-0 min-h-[30px]">
            <Checkbox
              size="small"
              checked={selectedFilters[category]?.includes(item) || false}
              onChange={() => onFilterChange(category, item)}
            />
            <ListItemText primary={item} className="text-sm" />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <div className="hidden lg:block w-64 p-4 bg-gray-50 border-r border-gray-200 sticky top-0 h-full overflow-y-auto">
      <h3 className="text-xl font-bold text-amber-900 mb-4 border-b pb-2">Filters</h3>

      {/* Stone / Metal Filter */}
      {renderCheckboxes('stone_metal', 'Metal / Stone Type')}

      {/* Style Filter */}
      {renderCheckboxes('style', 'Style / Length')}
      
    </div>
  );
};


// ---
// Main Mangalsutra Page Component
// ---

const MangalsutraPage: React.FC = () => {
  const [allProducts, setAllProducts] = useState<Product[]>([]); 
  const [loading, setLoading] = useState(true); 
  const [error, setError] = useState<string | null>(null); 
  const [sortBy, setSortBy] = useState('latest');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
 const { addItemToCart } = useShop();
  // --- Data Fetching Logic ---
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        let goldProducts: Product[] = [];
        let silverProducts: Product[] = [];
  
        // Fetch Gold Products
        const goldResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/gold-products');
        goldProducts = goldResponse.data.map(goldItem => ({
          id: goldItem.srno,
          itemId: goldItem.itemId,
          imageUrl: goldItem.images && goldItem.images.length > 0
            ? goldItem.images[0].imagePath
            : 'https://dummyimage.com/300x350/cccccc/000000&text=No+Image', 
          name: goldItem.itemName,
          price: goldItem.price,
          stock: goldItem.stockQuantity,
          category: goldItem.category || 'N/A', 
          rating: 4.5, 
          style: goldItem.style || 'N/A', 
          stone_metal: 'Gold', // Assuming Gold
        }));
  
        // Fetch Silver Products (though Mangalsutras are usually gold/black beads, we fetch silver too for completeness)
        const silverResponse = await axios.get<any[]>('https://sardar-jewellers.com/api/admin/silver-products');
        silverProducts = silverResponse.data.map(silverItem => ({
          id: silverItem.srno,
          itemId: silverItem.itemId,
          imageUrl: silverItem.images && silverItem.images.length > 0
            ? silverItem.images[0].imagePath
            : 'https://dummyimage.com/300x350/cccccc/000000&text=No+Image',
          name: silverItem.itemName,
          price: silverItem.price,
          stock: silverItem.stockQuantity,
          category: silverItem.category || 'N/A',
          rating: 4.2, 
          style: silverItem.style || 'N/A',
          stone_metal: 'Silver', // Assuming Silver
        }));
  
        setAllProducts([...goldProducts, ...silverProducts]);

      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load Mangalsutra products. Please ensure the backend is running.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []); 
  // ---------------------------------------------------------------------


  //  BASE FILTERING LOGIC: Only keep items whose category is 'Mangalsutra'
  const baseFilteredProducts = useMemo(() => {
      // Filter the fetched products down to only 'Mangalsutra'
      return allProducts.filter(product => {
          const category = product.category.toLowerCase();
          const name = product.name.toLowerCase();
          // Check for common variations
          return category.includes('mangalsutra') || 
                 name.includes('mangalsutra');
      });
  }, [allProducts]);

  // SECONDARY FILTERING LOGIC (Using Checkboxes)
  const finalFilteredProducts = useMemo(() => {
    let currentProducts = baseFilteredProducts;

    // Filter by Checkbox Categories
    Object.keys(selectedFilters).forEach(category => {
      const selectedValues = selectedFilters[category];
      
      if (selectedValues && selectedValues.length > 0) {
        currentProducts = currentProducts.filter(product => {
          // Check if the product property's value is included in the selected filters
          return selectedValues.includes(product[category as keyof Product] as string);
        });
      }
    });

    return currentProducts;
  }, [baseFilteredProducts, selectedFilters]);

  // SORTING LOGIC 
  const sortedAndFilteredProducts = useMemo(() => {
    const sorted = [...finalFilteredProducts].sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      if (sortBy === 'rating-desc') return b.rating - a.rating;
      return a.id - b.id; 
    });
    return sorted;
  }, [finalFilteredProducts, sortBy]);


  // FILTER CHANGE HANDLER
  const handleFilterChange = (category: string, value: string) => {
    setSelectedFilters(prev => {
      const currentValues = prev[category] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      
      if (newValues.length === 0) {
        const { [category]: removed, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [category]: newValues };
    });
  };

  // --- Render Loading/Error States ---
  if (loading) {
    return <div className="text-center py-20 text-xl text-amber-800">Loading Mangalsutras...</div>;
  }

  if (error) {
    return <div className="text-center py-20 text-xl text-red-600">{error}</div>;
  }
  
  // --- Render Main Page ---
  return (
    <div className="flex flex-col min-h-screen bg-white">
      
      {/*  Banner Section with Background Image */}
      <div 
        className="w-full h-[350px] text-white p-4 py-6 md:py-8 lg:py-10 shadow-lg relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${MANGALSUTRA_BANNER_IMAGE})` }}
      >
        

        {/* <div className="relative z-10 text-center pt-24">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold font-[Cinzel] mb-2 text-amber-50">
                Mangalsutra Collection
            </h1>
            <p className="text-md sm:text-lg font-light text-amber-100">
                A sacred thread of love, available in traditional and modern designs.
            </p>
        </div> */}
      </div>
      
      {/* Main Content Area: Sidebar + Products */}
      <div className="flex flex-1"> 
        
        {/* Sidebar - Filters */}
        <FilterSidebar 
          filters={filterOptions} 
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
        />
        
        {/* Products Display */}
        <div className="flex-1 p-4 lg:p-8">
          
          {/* Header and Sort By */}
          <div className="flex justify-between items-center mb-6 border-b pb-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-amber-800">
                 Showing  Mangalsutras
            </h1>
            
            <FormControl variant="outlined" size="small" className="min-w-[120px] bg-white">
              <InputLabel id="sort-by-label">Sort by</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as string)}
                label="Sort by"
                sx={{ 
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#b45309 !important' }, 
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a16207 !important' }
                }} 
              >
                <MenuItem value="latest">Latest</MenuItem>
                <MenuItem value="price-asc">Price: Low to High</MenuItem>
                <MenuItem value="price-desc">Price: High to Low</MenuItem>
                <MenuItem value="rating-desc">Rating</MenuItem>
              </Select>
            </FormControl>
          </div>
          
          {/* Products Grid */}
          {sortedAndFilteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {sortedAndFilteredProducts.map(product => (
                            <ProductCard 
        key={product.id} 
        product={product} 
        onAddToCart={addItemToCart} 
      />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-lg text-gray-600">
                No Mangalsutras found matching your filters.
            </div>
          )}
          
        </div>
        
      </div>
      
    </div>
  );
};

export default MangalsutraPage;